import utils from '@wtdc/utils';
import { EMPTY_PAGE_BEAN } from '@/constants/common';
import dayjs from 'dayjs';
import uuid from '@wtdc/shortid';
import md5 from 'md5';

const Utils = Object.assign({}, utils, {
  addCommas(num = 0) {
    let res = num.toString();
    return res.replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
  },

  isEmptyObject(value) {
    for(let p in value) {
      return !1;
    }
    return !0;
  },

  isEmpty(value) {
    return (value === undefined || value === null || value === '');
  },

  searchAssign(value = {}) {
    let _copy = Object.assign({}, value);
    for(let key in value) {
      if(Utils.isEmpty(value[key])) {
        delete _copy[key];
      }
    }
    return _copy;
  },
  supportsPassive() {
    let supportsPassive = false;
    try {
      let opts = Object.defineProperty({}, 'passive', {
        get: function() {
          return supportsPassive = true;
        }
      });
      window.addEventListener('test', null, opts);
    } catch(e) {}
    return supportsPassive;
  },
  /**
   * 将查询对象转换为查询字符串 （对参数中的http链接做了处理）
   * @param {Object} query 参数对象
   * @returns
   */
  formatQuery2QueryStr(query = {}) {
    let queryStr = '';
    const filteredQuery = Utils.searchAssign(query);
    if(Utils.isNotEmpty(filteredQuery)) {
      let q = {};
      Object.keys(filteredQuery).forEach(k => {
        const v = `${filteredQuery[k]}`;
        const i = v.indexOf('://'); // `http://`  `https://`
        if(i > -1 && i < 6) {
          q[k] = encodeURIComponent(v);
        } else {
          q[k] = v;
        }
      });
      queryStr = '?' + Utils.json2params(q);
      // queryStr = '?' + Utils.json2params(filteredQuery);
    }
    return queryStr;
  },
  /**
   * 过滤query中的参数
   * @param url: 要过滤query的url
   * @param filters<String|Array>: 要过滤的参数key值
   * @returns {string}: 过滤query后的url
   */
  filterQuery(url, filters = []) {
    let _filters = [].concat(filters);
    const [pathname, search] = url.split('?');
    const query = Utils.params2json(search);
    for(let filter of _filters) {
      delete query[filter];
    }
    return Utils.padQuery(pathname, query);
  },

  padQuery(url = '', params = {}) {
    const newUrl = Utils.decodeUrl(url);
    const [pathname, queryStr] = newUrl.split('?');
    let tempQuery = Utils.searchAssign(Object.assign({}, Utils.params2json(queryStr), Utils.searchAssign(Object.assign({}, params))));
    let searchQuery = Utils.formatQuery2QueryStr(tempQuery);
    return `${pathname}${searchQuery}`;
  },

  /**
   * decode处理 url
   * @param {String} url 源链接 http:// | https:// | / 开头
   */
  decodeUrl(url = '') {
    if(url && ((url.indexOf('%3A%2F%2F') > -1 && url.indexOf('%3A%2F%2F') < 6) || url.startsWith('%2F'))) {
      return decodeURIComponent(url);
    }
    return url;
  },
  // 判断数据是否为空，多用于数组和对象的判断
  isNotEmpty(value) {
    switch (typeof (value)) {
      case 'undefined': {
        return false;
      }

      case 'string': {
        return value.length !== 0;
      }

      case 'object': {
        if(Array.isArray(value)) {
          return value.length !== 0;
        } else if(value === null) {
          return false;
        } else {
          return Object.keys(value).length !== 0;
        }
      }

      default: {
        return true;
      }
    }
  },

  /**
   * 获取分页数据的加载状态
   */
  getPageLoadStatus(pageBean = EMPTY_PAGE_BEAN) {
    const empty = pageBean.totalCount === 0;
    return {
      empty,
      loading: pageBean.recordList.length === 0 && !empty,
      allLoaded: pageBean.recordList.length === pageBean.totalCount
    };
  },

  /**
   * encode处理 url
   * @param {String} url 源链接
   */
  encodeUrl(url = '') {
    if(url && ((url.indexOf('%3A%2F%2F') > -1 && url.indexOf('%3A%2F%2F') < 6) || url.startsWith('%2F'))) {
      return url;
    }
    return encodeURIComponent(url);
  },

  browser() {
    const ua = navigator.userAgent.toLowerCase();
    return {
      ios: !!ua.match(/\(i[^;]+;( U;)? CPU.+Mac OS X/i),
      android: ua.indexOf('android') > -1 || ua.indexOf('linux') > -1,
      iPad: ua.indexOf('ipad') > -1,
      wechat: ua.toLowerCase().indexOf('micromessenger') > -1,
      raysVisionWechat: ua.toLowerCase().indexOf('micromessenger') > -1 && !!sessionStorage.getItem('FROM_RAYS_VISION_WEAPP'),
      aliPay: ua.toLowerCase().indexOf('alipay') > -1,
      safari: ua.indexOf('safari') > -1 && !(ua.indexOf('browser') > -1),
      douyin: ua.toLowerCase().indexOf('aweme') > -1,
      /** 抖音极速版 */
      douyinLite: ua.toLowerCase().indexOf('aweme_lite') > -1,
      douyinMini: ua.indexOf('toutiaomicroapp') > -1, // 抖音小程序webview
      qq: (/qqbrowser/.test(ua) || / qq/.test(ua)) && !(ua.toLowerCase().indexOf('micromessenger') > -1) // qq浏览器
    };
  },

  toFixed(num = 0, len = 0) {
    let result = '',
      level = 3,
      [tempNum, tail] = `${num}`.split('.'),
      showText = false;
    if(num >= 1000000) {
      showText = true;
      let tempCount = (num / 10000).toFixed(2);
      [tempNum, tail] = `${tempCount}`.split('.');
    }
    const padEnd = (str, len = 2, padString = 0) => {
      str = str.slice(0, len);
      while(str.length < len) {
        str += padString;
      }
      return str;
    };
    if(!len) {
      tail = tail ? padEnd(tail) : '00';
      tempNum = (tempNum || 0).toString();
      while(tempNum.length > level) {
        result = ',' + tempNum.slice(-level) + result;
        tempNum = tempNum.slice(0, tempNum.length - level);
      }
      if(tempNum) {
        result = tempNum + result;
      }
      return `${result}.${tail}${showText ? '万' : ''}`;
    } else {
      if(typeof num !== 'number') {
        num = Number(num);
      }
      return num.toFixed(len);
    }
  },
  /** 是否直接进客服 (或先进提示页) */
  //  isDirectToCustomer() {
  //   const storedTime = Storage.getSync(NUMBER_DIRECT_TO_CUSTOMER);
  //   const isDirect = Date.now() - storedTime < 604800000; // 7 * 24 * 60 * 60 * 1000
  //   return isDirect;
  // },
  // getVersionInfo() {
  //   const sysInfo = Utils.getSystemInfo();
  //   let versionInfo = Storage.getSync(APP_VERSION) || {};
  //   const { emergencyStatus, androidEmergency } = versionInfo;
  //   let showEmergency = sysInfo.isIOS ? emergencyStatus : androidEmergency;
  //   return Object.assign({}, versionInfo, {
  //     service2Customer: sysInfo.isIOS && Number(versionInfo.ios2service) === 0,
  //     isAppeal: Number(showEmergency) === 1
  //     // appealTipsHandle: ()=>{
  //     //   // return Taro.showModal({ title: '请重新扫书上二维码获取资源' });
  //     // }
  //   });
  // },

  checkSupportWebP() {
    return (
      document
        .createElement('canvas')
        .toDataURL('image/webp')
        .indexOf('data:image/webp') === 0
    );
  },

  /**
   * https://help.aliyun.com/document_detail/50039.html?spm=5176.doc31848.6.750.BrFGlG
   * @param src
   * @param ratio 图片缩放比例 0-1
   * @param effect 图片处理效果例如{resize:'w_100,h_100'}
   * @returns {string}
   */
  getImg(src = '', ratio = 0.8, effect = {}) {
    let baseUrl = src;
    let queryStr = '';
    let query = {};
    let queryIndex = src.indexOf('?');
    if(queryIndex > -1) {
      baseUrl = src.slice(0, queryIndex);
      queryStr = src.slice(queryIndex + 1);
    }
    if(queryStr) {
      query = Utils.params2json(queryStr);
    }
    const isGIF = /(\.gif|GIF)$/.test(baseUrl);
    effect = Object.assign(
      {
        'auto-orient': 0
      },
      effect
    );
    const handleOssImg = (url = '', ratio, effects = {}) => {
      const TYPE = 'image';
      const EFFECTSKEY = 'x-oss-process';
      let effectsStr = '';
      if(query[EFFECTSKEY]) {
        effectsStr = query[EFFECTSKEY];
      }
      const str2actions = (actionsStr = effectsStr) => {
        let actionsArr = actionsStr.split('/');
        let actions = {};
        if(actionsArr.length > 1) {
          actionsArr.slice(1).forEach((item = '') => {
            let keyIndex = item.indexOf(',');
            let key = item.slice(0, keyIndex),
              value = item.slice(keyIndex + 1);
            actions[key] = value;
          });
        }
        return actions;
      };
      const actions2str = (actions = {}) => {
        let str = '';
        for(let key in actions) {
          str += `/${key},${actions[key]}`;
        }
        return str;
      };
        //因图片宽或者高超出4096时旋转后不显示，因此超出限制时显示最大值
      if(url.indexOf('4096') < 0 && !effects.resize) {
        if(ratio > 0) {
          ratio = ratio >= 1 ? 1 : ratio;
          Object.assign(effects, {
            resize: `p_${Math.round(ratio * 100)}`,
            quality: `q_${effects.quality || 100}`
          });
        }
      }
      if(Utils.checkSupportWebP()) {
        Object.assign(effects, { format: 'webp' });
      }
      if(Object.keys(effects).length) {
        let resultActions = `${TYPE}${actions2str(Object.assign({}, str2actions(), effects))}`;
        if(effectsStr) {
          let reg = new RegExp(effectsStr, 'g');
          return url.replace(reg, resultActions);
        } else {
          return `${baseUrl}?${Utils.json2params(Object.assign({}, query, {
            [EFFECTSKEY]: resultActions
          }))}`;
        }
      } else {
        return url;
      }
    };

    if(
      !isGIF // gif不处理
        && ((src && src.indexOf('/oss/') > -1) || (src && src.indexOf('/ios/') > -1))
    ) {
      return handleOssImg(src, ratio, effect);
    } else {
      return src;
    }
  },
  cutNum2Str(number = 0, limit = 99, type = '+') {
    return number >= limit ? `${limit}${type}` : number;
  },
  timeFormat(time = 0) {
    let hours,
      minutes,
      seconds;
    let intTime = Math.floor(time);
    hours = Math.floor(intTime / 3600);
    minutes = Math.floor(intTime / 60 % 60);
    seconds = intTime % 60;
    return {
      hours: hours,
      minutes: (minutes > 9) ? minutes : '0' + minutes,
      seconds: (seconds > 9) ? seconds : '0' + seconds
    };
  },
  /**
   * 获取阿里云原始文件
   * @param url
   * @returns {string}
   */
  getOriginFile(url = '') {
    if(url.indexOf('//oss') > -1) {
      return url.replace('//oss', '//file');
    } else {
      return url;
    }
  },
  previewImage({ urls = [], idx = 0, current = '' }) {
    let showUrl = current || urls[idx];
    let showUrls = urls.map(v => Utils.getOriginFile(v));
    let showCurrent = Utils.getOriginFile(showUrl);
    if(wx && Utils.browser().wechat) {
      wx.previewImage({
        current: showCurrent,
        urls: showUrls
      });
    }
  },

  /**
   * 社群书书名检测
   */
  showBookName(name = '') {
    return /[《》]/g.test(name) ? name : `《${name}》`;
  },

  getDate(time) {
    const d = time ? new Date(time) : new Date();
    const year = d.getFullYear(),
      month = d.getMonth(),
      day = d.getDate(),
      week = d.getDay(),
      hours = d.getHours(),
      minutes = d.getMinutes(),
      seconds = d.getSeconds(),
      ms = d.getMilliseconds();
    return {
      Y: year,
      M: month,
      W: week,
      D: day,
      h: hours,
      min: minutes,
      s: seconds,
      ms: ms
    };
  },

  /**
   * 格式化时间
   * 用"YY","MM","DD","hh","mm","ss"分别表示年、月、日、时、分、秒  (如 YY-MM-DD hh:mm:ss)
   * @param {Date} date 待格式化的时间
   * @param {String} format 格式： example: YY-MM-DD 2021-01-06, YY/MM/DD 2021/01/06
   * @returns {String} 格式化后的时间
   */
  dateFormat(date, format = 'YY-MM-DD') {
    const _date = Utils.getDate(date);
    const _newDate = {
      YY: String(_date.Y),
      MM: String(_date.M + 1).padStart(2, '0'),
      DD: String(_date.D).padStart(2, '0'),
      hh: String(_date.h).padStart(2, '0'),
      mm: String(_date.min).padStart(2, '0'),
      ss: String(_date.s).padStart(2, '0')
    };
    // dateFormat 方法中，匹配日期格式
    const DATE_FORMAT_REG = /(YY|MM|DD|hh|mm|ss)/g;
    return format.replace(DATE_FORMAT_REG, function(match, p) {
      return _newDate[p];
    });
  },
  /**
   * 格式化时间
   * @param date
   * @param gap
   * @param requireTime
   * @param requireDate
   * @returns {string}
   */
  date2string(date, gap = '-', requireTime = false, requireDate = true) {
    let tempObj = Utils.getDate(date);
    for(let key in tempObj) {
      if(key === 'M') {
        tempObj[key]++;
      }
      tempObj[key] = Utils.padStart(`${tempObj[key]}`, 2, '0');
    }
    const { Y, M, D, h, min, s } = tempObj;
    const Dd = `${Y}${gap}${M}${gap}${D}`, T = `${h}:${min}:${s}`;
    let result = requireDate ? `${Dd} ` : '';
    requireTime && (result += T);
    return result.trim();
  },
  padStart(string = '', maxLength = 0, fillString = '0') {
    string = String(string);

    // native padStart
    if(typeof String.prototype.padStart === 'function') {
      return string.padStart(maxLength, fillString);
    }

    // loop to pad start
    while(string.length < maxLength) {
      string = fillString + string;
    }

    return string;
  },
  /**
   * 将时间格式化
   * @param seconds 时间，单位：秒
   * @param format 时间的展示格式
   * @returns {string}
   */
  formatSeconds(seconds = 0, format = 'mm:ss') {
    const _seconds = Math.round(seconds);
    const hours = Math.floor(_seconds / 3600);
    const minutes = Math.floor((_seconds % 3600) / 60);
    const secs = _seconds % 60;
    const regs = [HOUR_REG, MINUTE_REG, SECOND_REG];
    const times = [hours, minutes, secs];

    let result = format;
    regs.forEach((reg, index) => {
      result = result.replace(reg, (match) => {
        const length = match.length;
        if(length === 0) {
          return '';
        } else {
          return Utils.padStart(String(times[index]), length, '0');
        }
      });
    });

    return result;
  },

  //浏览器类型检测
  userAgent() {
    let navigator = window.navigator;
    let agent = navigator.userAgent.toLowerCase();
    let system = {};
    //userAgent
    system.agent = agent;
    // 取浏览器语言
    system.currentLang = navigator.language || navigator.browserLanguage;
    // 判断浏览器类型
    let browserName = { Safe: '360安全浏览器-极速模式', Fast: '360极速浏览器-极速模式', EMC: '360兼容模式' };
    let browserArray = {
      IE: window.ActiveXObject || 'ActiveXObject' in window, // IE
      Chrome: agent.indexOf('chrome') > -1 && agent.indexOf('safari') > -1, // Chrome浏览器
      Firefox: agent.indexOf('firefox') > -1, // 火狐浏览器
      Opera: agent.indexOf('opera') > -1, // Opera浏览器
      Safari: agent.indexOf('safari') > -1 && agent.indexOf('chrome') === -1, // safari浏览器
      Edge: agent.indexOf('edge') > -1, // Edge浏览器
      Safe: agent.indexOf('wow') !== -1 && agent.indexOf('net') < 0 && agent.indexOf('firefox') < 0 && navigator.javaEnabled(), // 360安全浏览器-极速模式
      Fast: agent.indexOf('wow') !== -1 && agent.indexOf('net') < 0 && agent.indexOf('firefox') < 0, // 360极速浏览器-极速模式
      EMC: agent.indexOf('wow') !== -1 && agent.indexOf('net') !== -1 && agent.indexOf('msie') !== -1 && agent.indexOf('rv') < 0, // 360兼容模式
      QQBrowser: /qqbrowser/.test(agent), // qq浏览器
      WeixinBrowser: /microMessenger/i.test(agent) // 微信浏览器
    };
    for(let i in browserArray) {
      if(browserArray[i]) {
        system.browser = browserName[i] || i;
      }
    }
    // IE浏览器版本号
    if(system.browser === 'IE') {
      let re = new RegExp('msie ([0-9]{1,})');
      let re11 = new RegExp('rv\:([0-9]{1,})');
      if(re.exec(agent) != null) {
        system.version = RegExp.$1;
      } else { // IE11
        if(re11.exec(agent) != null) {
          system.version = RegExp.$1;
        }
      }
    }
    // 获取操作系统
    let OSArray = {};
    OSArray.Windows = (navigator.platform === 'win32') || (navigator.platform === 'windows');
    OSArray.Mac = (navigator.platform === 'mac68K') || (navigator.platform === 'macppc') || (navigator.platform === 'Macintosh') || (navigator.platform === 'MacIntel');
    OSArray.iphone = agent.indexOf('iphone') > -1;
    OSArray.ipod = agent.indexOf('ipod') > -1;
    OSArray.ipad = agent.indexOf('ipad') > -1;
    OSArray.Android = agent.indexOf('android') > -1;
    for(let i in OSArray) {
      if(OSArray[i]) {
        system.OS = i;
      }
    }
    return system;
  },
  //获取网络状态接口
  getNetworkType() {
    const ap = window.ap || {};//beta版本js-sdk，优先使用这个
    return new Promise((resolve, reject) => {
      let api = wx ? wx.getNetworkType : (ap ? ap.getNetworkType : null);
      if(api) {
        api({
          success: function(res) {
            resolve(`${res.networkType}`.toLowerCase());      // 返回网络类型2g，3g，4g，wifi
          },
          fail: function(err) {
            reject(err);
          }
        });
      } else {
        reject('get network type error');
      }
    });
  },
  getFileExt: (fileName = '') => {
    const index = fileName.lastIndexOf('.');
    const regExp = new RegExp('^[A-Za-z0-9]+$');
    if(index > 0 && regExp.test(fileName.slice(index + 1))) {
      fileName = fileName.toLowerCase();
      return fileName.substring(index + 1);
    }
    return '';
  },

  /**
   * 节流函数，在指定周期内最多只执行一次函数
   * @param func<Function>: 要节流的函数
   * @param interval<Number>: 节流周期
   */
  throttle(func, interval = 500) {
    let last = 0;
    let timer = null;
    return function(...args) {
      const now = Date.now();
      const diff = now - last;

      const execute = () => {
        last = now;
        func(...args);
      };

      if(diff >= interval) {
        window.clearTimeout(timer);
        execute();
      } else {
        window.clearTimeout(timer);
        timer = window.setTimeout(() => {
          timer = null;
          execute();
        }, interval - diff);
      }
    };
  },
  // 文件是否为阿里云文件
  isOSSFile(url) {
    return url.indexOf('/oss/') >= 0;
  },

  /**
   * 判断颜色为深色 or 浅色
   * @param color：颜色值，目前支持#aaa & #aaaaaa & rgb(170, 170, 170) & rgba(170, 170, 170, 0.4)四种格式
   * @return Boolean：深色为true，浅色为false
   * **/
  isDarkColor: (color = '') => {
    let colors = [];
    let _rgb = color;
    if(_rgb.startsWith('#')) {
      _rgb = _rgb.slice(1);
      if(_rgb.length === 3) {
        colors = [...color].map((color) => Number(`Ox${color}${color}`));
      } else if(_rgb.length === 6) {
        colors = new Array(3).fill('').map((_, idx) => Number(`0x${_rgb.slice(idx * 2, (idx + 1) * 2)}`));
      }
    } else if(_rgb.startsWith('rgb(')) {
      colors = _rgb.slice(4, -1).split(',').map(v => Number(v));
    } else if(_rgb.startsWith('rgba(')) {
      colors = _rgb.slice(5, -1).split(',').map(v => Number(v));
    }

    const opacity = colors[3];
    // 透明度小于等于0.5，判定为浅色
    if(opacity && opacity <= 0.5) {
      return false;
    }
    // rgb只要有一个色值小于180，即为深色
    return colors.slice(0, 3).some((color) => color < 0xB4);
  },
  //截取字符串
  cutString(str = '', len, type = '...') {
    if(str.length > len) {
      return str.slice(0, len) + type;
    } else return str;
  },
  isWeAppEnv() {
    return window.__wxjs_environment && window.__wxjs_environment === 'miniprogram';
  },

  /**
   * 防抖函数
   * @param func
   * @param delay
   */
  debounce(func, delay = 100) {
    let timer = null;
    return (...args) => {
      // 当delay等于0时，取消debounce，改为同步执行该方法
      if(delay === 0) {
        return func(...args);
      }

      clearTimeout(timer);
      timer = setTimeout(() => {
        func(...args);
      }, delay);
    };
  },
  /**
   * 对比两个对象的值是否完全相等
   * @param {Object} a
   * @param {Object} b
   * @returns {Boolean}
   */
  isObjectValueEqual(a, b) {
    if(
      Object.prototype.toString.call(a) !== '[object Object]' ||
      Object.prototype.toString.call(b) !== '[object Object]'
    ) {
      console.warn('Invalid parameter.');
      return;
    }
    // 取对象a和b的属性名
    let aProps = Object.getOwnPropertyNames(a);
    let bProps = Object.getOwnPropertyNames(b);
    // 判断属性名的length是否一致
    if(aProps.length !== bProps.length) {
      return false;
    }
    // 循环取出属性名，再判断属性值是否一致
    for(let i = 0; i < aProps.length; i++) {
      let propName = aProps[i];
      if(a[propName] !== b[propName]) {
        return false;
      }
    }
    return true;
  },
  client() {
    const u = navigator.userAgent;
    return {
      webKit: u.indexOf('AppleWebKit') > -1, //苹果、谷歌内核
      mobile: !!u.match(/AppleWebKit.*Mobile.*/), //是否为移动终端
      ios: !!u.match(/\(i[^;]+;( U;)? CPU.+Mac OS X/), //ios终端
      android: u.indexOf('Android') > -1 || u.indexOf('Linux') > -1, //android终端
      iPhone: u.indexOf('iPhone') > -1, //是否为iPhone或者QQHD浏览器
      iPad: u.indexOf('iPad') > -1, //是否iPad
      ie: window.ActiveXObject || 'ActiveXObject' in window,
      wechat: u.indexOf('MicroMessenger') > -1 //是否微信 （2015-01-22新增）
    };
  },
  /**
   * 将数组按每组固定数量拆分
   * @param {Array} list 原数组
   * @param {Number} numPerGroup 每组元素个数
   */
  splitToGroups(list = [], numPerGroup = 2) {
    let _list = [], index = 0;
    while(list.length > index) {
      _list.push(list.slice(index, index + numPerGroup));
      index = index + numPerGroup;
    }
    return _list;
  },

  //获取node的当前property属性值
  getCurProperty(node, property) {
    return node.currentStyle ? node.currentStyle[property] : getComputedStyle(node)[property];
  },
  /**
   * 获取 指定日期 n 天以前/以后的日期
   * @param {Date} theDate 开始日期
   * @param {Number} nDays 日期差：正数为以后，负数为之前
   * @param {String} format 格式化参数: YY MM DD hh mm ss
   */
  getTheDate(theDate = null, nDays = 0, format = 'YY-MM-DD') {
    const _date = theDate ? +new Date(theDate) : Date.now();
    const newDate = _date + nDays * 86400000;
    return Utils.dateFormat(newDate, format);
  },

  createEncryption() {
    let uuidStr = uuid();
    uuidStr = uuidStr.replace(/-/g, 'h'); //去掉随机字符串里面的-
    let nonceStr = `${uuidStr}-${Date.now()}`;
    let md5Signature = md5(nonceStr + 'RAYS5.0yfzx');
    let signature = `${md5Signature}-${nonceStr}`;
    return {
      signature
    };
  },
  //滑动到指定位置
  scrollToTop(scrollTop = 0, location = 0, drag = 10) {
    // 阻力，数值越大，滑动越慢
    // 默认距离顶部的距离
    const gap = document.documentElement.scrollTop || document.body.scrollTop;
    if(gap > location) {
      window.requestAnimationFrame(Utils.scrollToTop);
      window.scrollTo(scrollTop, gap - gap / drag);
    }
  },
  getEllipsis(str, len = 6) {
    if(typeof str === 'string' && str.length > len) {
      return str.slice(0, len) + '...';
    } else {
      return str;
    }
  },
  isObject(temp) {
    return Object.prototype.toString.call(temp) === '[object Object]';
  },

  classNames(...args) {
    const classnames = args.filter((name) => !!name);
    return classnames.map(name => {
      if(Utils.isObject(name)) {
        const temps = [];
        Object.keys(name).forEach(v => {
          if(name[v]) {
            temps.push(v);
          }
        });
        name = temps.join(' ');
      }
      return name;
    }).join(' ');
  },
  getWeekTime(date) {
    let new_Date = new Date(date || new Date()); //获取本周一周日期
    let timesStamp = new_Date.getTime();
    let currenDay = new_Date.getDay();
    let dates = [];
    for(let i = 0; i < 7; i++) {
      let das = new Date(timesStamp + 24 * 60 * 60 * 1000 * (i - (currenDay + 6) % 7)).toLocaleDateString();
      das = das.replace(/\//g, '-');
      das = dayjs(das).format('YYYY-MM-DD');
      dates.push(das);
    }
    return dates;
  },
  strip(num, precision = 12) {
    return +parseFloat(num.toPrecision(precision));
  },
  /**
   * 删除或者替换locaiton的query
   * appendQuery里面传的key-value,value仅支持基本类型，不支持object/array
   * 如果value为undefined或者null代表删除key
   * @param {*} _history
   * @param {*} _location
   * @param {*} originalQuery
   * @param {*} appendQuery
   * @returns
   */
  replaceLocationQuery(_history, _location, originalQuery, appendQuery) {
    let changed = false;
    originalQuery = Utils.searchAssign(originalQuery);
    const newQuery = { ...originalQuery };
    appendQuery = (appendQuery || {});
    for(const key in appendQuery) {
      let value = appendQuery[key];
      if(value !== originalQuery[key]) {
        newQuery[key] = value;
        changed = true;
      }
    }
    if(!changed) {
      return { changed: false, query: originalQuery };
    }
    const targetQuery = Utils.searchAssign(newQuery);
    const replaces = [];
    const deletes = [];
    const appends = [];
    for(const key in targetQuery) {
      if(key in originalQuery) {
        replaces.push(key);
      } else {
        appends.push(key);
      }
    }
    for(const key in originalQuery) {
      if(!(key in targetQuery)) {
        deletes.push(key);
      }
    }
    let search = _location.search;
    const values = search.length > 1 ? search.slice(1).split('&') : [];
    replaces.forEach(key => {
      const targetStr = `${key}=${encodeURIComponent(targetQuery[key])}`;
      const start = `${key}=`;
      const idx = values.findIndex(v => v.indexOf(start) === 0);
      if(idx !== -1) {
        values[idx] = targetStr;
      } else {
        console.error(`replaceLocationQuery, replace item ${key} can't found`);
      }
    });
    deletes.forEach(key => {
      const start = `${key}=`;
      const idx = values.findIndex(v => v.indexOf(start) === 0);
      if(idx !== -1) {
        values.splice(idx, 1);
      } else {
        console.error(`replaceLocationQuery, delete item ${key} can't found`);
      }
    });
    appends.forEach(key => {
      values.push(`${key}=${encodeURIComponent(targetQuery[key])}`);
    });
    if(values.length > 0) {
      search = `?${values.join('&')}`;
    } else {
      search = '';
    }
    _history.replace(_location.pathname + search + _location.hash);
    return { changed: true, query: targetQuery };
  }
});

export default Utils;

const HOUR_REG = /h{1,2}/gm;
const MINUTE_REG = /m{1,2}/gm;
const SECOND_REG = /s{1,2}/gm;
// 是否直接进客服(或先进提示页) 不提示时间记录
// const NUMBER_DIRECT_TO_CUSTOMER = 'NUMBER_DIRECT_TO_CUSTOMER';

